import { useState, useCallback, useRef } from 'react';
import { useEditorStore } from '../store/editorStore';
import { usePlayheadStore } from '../store/playheadStore';
import { saveProxy } from '../engine/proxyManager';
import { extractPeaks } from '../engine/peakGenerator';
import { saveWaveform } from '../engine/proxyManager';
import { initAudioAsset } from '../renderer/rendererBridge';

interface RecordingState {
  recording: boolean;
  deviceId: string | null;
  devices: MediaDeviceInfo[];
  livePeaks: Float32Array | null;
}

export function useRecording() {
  const [state, setState] = useState<RecordingState>({
    recording: false,
    deviceId: null,
    devices: [],
    livePeaks: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef(0);

  const getDevices = useCallback(async () => {
    await navigator.mediaDevices.getUserMedia({ audio: true });
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((d) => d.kind === 'audioinput');
    setState((s) => ({ ...s, devices: audioInputs }));
    return audioInputs;
  }, []);

  const selectMic = useCallback((deviceId: string) => {
    setState((s) => ({ ...s, deviceId }));
  }, []);

  const startRecording = useCallback(async (trackId?: string) => {
    const constraints: MediaStreamConstraints = {
      audio: state.deviceId ? { deviceId: { exact: state.deviceId } } : true,
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    streamRef.current = stream;

    // Set up analyser for live peaks
    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    // Live peak visualization
    const dataArray = new Float32Array(analyser.frequencyBinCount);
    function updatePeaks() {
      if (!analyserRef.current) return;
      analyserRef.current.getFloatTimeDomainData(dataArray);
      setState((s) => ({ ...s, livePeaks: new Float32Array(dataArray) }));
      rafRef.current = requestAnimationFrame(updatePeaks);
    }
    rafRef.current = requestAnimationFrame(updatePeaks);

    // Record
    const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      cancelAnimationFrame(rafRef.current);
      analyserRef.current = null;
      stream.getTracks().forEach((t) => t.stop());
      await audioCtx.close();

      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const arrayBuffer = await blob.arrayBuffer();

      // Create asset
      const store = useEditorStore.getState();
      const assetId = crypto.randomUUID();

      // Decode to get duration
      const decodeCtx = new AudioContext();
      const audioBuf = await decodeCtx.decodeAudioData(arrayBuffer.slice(0));
      const durationMs = Math.round(audioBuf.duration * 1000);
      await decodeCtx.close();

      // Generate peaks
      const peakData = extractPeaks(audioBuf, 256);

      // Save to IDB
      await saveProxy(assetId, arrayBuffer);
      await saveWaveform(assetId, peakData);

      // Init audio engine with this recording's buffer
      await initAudioAsset(assetId, arrayBuffer);

      // Add asset to store
      store.addAsset({
        id: assetId,
        filename: `Recording ${new Date().toLocaleTimeString()}`,
        mimeType: 'audio/webm',
        originalUrl: '',
        durationMs,
        width: 0,
        height: 0,
        fps: 0,
        ingestStatus: 'ready',
        ingestProgress: 100,
        proxyKey: `editor-proxy:${assetId}`,
        waveformKey: `editor-waveform:${assetId}`,
        mediaType: 'audio',
      });

      // Add to timeline
      const targetTrackId = trackId ?? store.addTrack('audio', 'Recording');
      const startMs = usePlayheadStore.getState().currentTimeMs;
      store.addClip(targetTrackId, {
        assetId,
        startMs,
        durationMs,
        sourceOffsetMs: 0,
      });

      setState((s) => ({ ...s, recording: false, livePeaks: null }));
    };

    mediaRecorderRef.current = recorder;
    recorder.start(100); // Collect data every 100ms
    setState((s) => ({ ...s, recording: true }));
  }, [state.deviceId]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  return {
    ...state,
    getDevices,
    selectMic,
    startRecording,
    stopRecording,
  };
}
